import { Link } from 'react-router-dom';
import { ShieldCheck, Clock, BellRing, Server, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Landing = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div>
      {/* Hero section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
                Never miss an SSL certificate expiration again
              </h1>
              <p className="mt-4 text-lg md:text-xl text-primary-100">
                WeTrackSSL monitors your domains and sends you timely alerts before your SSL certificates expire, keeping your websites secure and your visitors protected.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row sm:space-x-4 space-y-4 sm:space-y-0">
                {isAuthenticated ? (
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50"
                  >
                    Go to Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/signup"
                      className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary-700 bg-white hover:bg-primary-50"
                    >
                      Start for Free
                    </Link>
                    <Link
                      to="/pricing"
                      className="inline-flex items-center justify-center px-5 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-primary-700"
                    >
                      View Pricing
                    </Link>
                  </>
                )}
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-primary-500 rounded-lg transform rotate-3 scale-105 opacity-20 animate-pulse"></div>
                <div className="relative bg-white rounded-lg shadow-xl overflow-hidden p-6">
                  <div className="flex items-center justify-center h-24 w-24 rounded-md bg-primary-500 text-white mx-auto">
                    <ShieldCheck className="h-12 w-12" />
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center">
                      <div className="h-3 w-3 rounded-full bg-accent-500 mr-2"></div>
                      <div className="h-2 bg-gray-200 rounded w-full"></div>
                    </div>
                    <div className="mt-3">
                      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                      <div className="mt-2 h-2 bg-gray-200 rounded w-full"></div>
                      <div className="mt-1 h-2 bg-gray-200 rounded w-5/6"></div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-accent-500">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          <span className="text-xs font-semibold">Certificate Valid</span>
                        </div>
                        <div className="text-xs text-gray-500">Expires in 45 days</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features section */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              How WeTrackSSL Works
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Our simple process ensures you never have to worry about SSL certificate expirations again.
            </p>
          </div>

          <div className="mt-12">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
              <div className="flex flex-col items-center text-center">
                <div className="flex items-center justify-center h-16 w-16 rounded-md bg-primary-100 text-primary-600">
                  <Server className="h-8 w-8" />
                </div>
                <h3 className="mt-6 text-xl font-medium text-gray-900">Add Your Domains</h3>
                <p className="mt-2 text-base text-gray-500">
                  Simply add the domains you want to monitor. No technical configuration required.
                </p>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="flex items-center justify-center h-16 w-16 rounded-md bg-primary-100 text-primary-600">
                  <Clock className="h-8 w-8" />
                </div>
                <h3 className="mt-6 text-xl font-medium text-gray-900">Automatic Monitoring</h3>
                <p className="mt-2 text-base text-gray-500">
                  We regularly check your SSL certificates and keep track of their expiration dates.
                </p>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="flex items-center justify-center h-16 w-16 rounded-md bg-primary-100 text-primary-600">
                  <BellRing className="h-8 w-8" />
                </div>
                <h3 className="mt-6 text-xl font-medium text-gray-900">Timely Alerts</h3>
                <p className="mt-2 text-base text-gray-500">
                  Receive notifications well before certificates expire, giving you time to renew them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      {/* <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900">
              Trusted by Developers and Businesses
            </h2>
          </div>
          <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white shadow-sm border border-gray-200 rounded-lg p-6"
              >
                <div className="flex items-center">
                  <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-lg font-bold">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-gray-900">{testimonial.name}</h3>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
                <p className="mt-4 text-gray-600">{testimonial.quote}</p>
              </div>
            ))}
          </div>
        </div>
      </div> */}

      {/* CTA section */}
      <div className="bg-primary-700">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">Ready to get started?</span>
            <span className="block text-primary-200">Start monitoring your domains today.</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0">
            <div className="inline-flex rounded-md shadow">
              <Link
                to={isAuthenticated ? "/dashboard" : "/signup"}
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50"
              >
                {isAuthenticated ? "Go to Dashboard" : "Get Started"}
              </Link>
            </div>
            <div className="ml-3 inline-flex rounded-md shadow">
              <Link
                to="/pricing"
                className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Testimonial data
// const testimonials = [
//   {
//     name: "Sarah Johnson",
//     role: "Web Developer",
//     quote: "WeTrackSSL has saved me countless times. I used to forget about certificate renewals until my clients called with site issues. Now I get alerts well before anything expires."
//   },
//   {
//     name: "Mark Thompson",
//     role: "IT Manager",
//     quote: "Managing dozens of domains across multiple projects used to be a nightmare. WeTrackSSL has simplified our certificate management and eliminated unexpected downtime."
//   },
//   {
//     name: "Emily Chen",
//     role: "Agency Owner",
//     quote: "The enterprise plan is perfect for our agency. We monitor all our client websites and never worry about embarrassing SSL expirations. Worth every penny!"
//   }
// ];

export default Landing;